/**
 * ContrattoTemplateForm Component
 * Feature: Legal - Form creazione/modifica template contratto
 */

import { useState, useEffect, useRef } from "react";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useContrattoTemplate, useCreateContrattoTemplate, useUpdateContrattoTemplate } from "../hooks";
import type { ContrattoTemplate } from "../types";

const TIPI = ["Affitto breve", "Affitto turistico", "B&B", "Locazione transitoria", "Altro"];

const PLACEHOLDER_GROUPS = [
  {
    label: "Ospite",
    tags: [
      { tag: "{{ospite_nome}}", desc: "Nome completo" },
      { tag: "{{ospite_cognome}}", desc: "Cognome" },
      { tag: "{{ospite_email}}", desc: "Email" },
      { tag: "{{ospite_telefono}}", desc: "Telefono" },
      { tag: "{{ospite_nazionalita}}", desc: "Nazionalità" },
      { tag: "{{ospite_documento}}", desc: "Num. documento" },
      { tag: "{{ospite_codice_fiscale}}", desc: "Codice fiscale" },
      { tag: "{{ospite_indirizzo}}", desc: "Indirizzo residenza" },
    ],
  },
  {
    label: "Soggiorno",
    tags: [
      { tag: "{{data_checkin}}", desc: "Data check-in" },
      { tag: "{{data_checkout}}", desc: "Data check-out" },
      { tag: "{{ora_checkin}}", desc: "Ora check-in" },
      { tag: "{{ora_checkout}}", desc: "Ora check-out" },
      { tag: "{{num_notti}}", desc: "Numero notti" },
      { tag: "{{num_ospiti}}", desc: "Numero ospiti" },
    ],
  },
  {
    label: "Struttura",
    tags: [
      { tag: "{{struttura_nome}}", desc: "Nome struttura" },
      { tag: "{{struttura_indirizzo}}", desc: "Indirizzo struttura" },
      { tag: "{{struttura_citta}}", desc: "Città" },
      { tag: "{{camera_nome}}", desc: "Nome camera/unità" },
    ],
  },
  {
    label: "Pagamento",
    tags: [
      { tag: "{{importo}}", desc: "Importo totale" },
      { tag: "{{importo_acconto}}", desc: "Acconto" },
      { tag: "{{importo_saldo}}", desc: "Saldo" },
      { tag: "{{data_pagamento}}", desc: "Data pagamento" },
      { tag: "{{metodo_pagamento}}", desc: "Metodo pagamento" },
    ],
  },
  {
    label: "Gestore",
    tags: [
      { tag: "{{host_nome}}", desc: "Nome host" },
      { tag: "{{host_email}}", desc: "Email host" },
      { tag: "{{host_telefono}}", desc: "Telefono host" },
      { tag: "{{data_firma}}", desc: "Data firma contratto" },
    ],
  },
];

interface ContrattoTemplateFormProps {
  id?: string;
  onBack?: () => void;
  onSaved?: (template: ContrattoTemplate) => void;
}

export function ContrattoTemplateForm({ id, onBack, onSaved }: ContrattoTemplateFormProps) {
  const isNew = !id || id === "nuovo";
  
  const { data: template, isLoading } = useContrattoTemplate(isNew ? undefined : id);
  const createMut = useCreateContrattoTemplate();
  const updateMut = useUpdateContrattoTemplate();

  const [form, setForm] = useState({
    nome: "",
    tipo: "Affitto breve",
    contenuto: "",
    attivo: true,
  });
  
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (template) {
      setForm({
        nome: template.nome ?? "",
        tipo: template.tipo ?? "Affitto breve",
        contenuto: template.contenuto ?? "",
        attivo: template.attivo ?? true,
      });
    }
  }, [template]);

  const set = (k: keyof typeof form, v: string | boolean) => setForm(f => ({ ...f, [k]: v }));

  const insertTag = (tag: string) => {
    const el = textareaRef.current;
    if (!el) { 
      set("contenuto", form.contenuto + tag); 
      return; 
    }
    const start = el.selectionStart ?? form.contenuto.length;
    const end = el.selectionEnd ?? start;
    const next = form.contenuto.slice(0, start) + tag + form.contenuto.slice(end);
    set("contenuto", next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + tag.length, start + tag.length);
    });
  };

  const handleSave = () => {
    if (!form.nome.trim()) return;
    setSaveError(null);

    const onSuccess = (res: ContrattoTemplate) => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onSaved?.(res);
    };
    
    const onError = (err: Error) => {
      setSaveError(err?.message ?? "Errore durante il salvataggio");
    };

    if (isNew) {
      createMut.mutate(form, { onSuccess, onError });
    } else {
      updateMut.mutate({ id: id!, data: form }, { onSuccess, onError });
    }
  };

  const handleBack = () => {
    if (onBack) onBack();
    else window.location.href = "/contratti";
  };

  // Loading skeleton
  if (!isNew && isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="w-9 h-9 rounded-xl" />
          <Skeleton className="h-6 w-48" />
        </div>
        <Skeleton className="h-40 rounded-2xl" />
        <Skeleton className="h-80 rounded-2xl" />
      </div>
    );
  }

  const isPending = createMut.isPending || updateMut.isPending;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button 
          onClick={handleBack}
          className="w-9 h-9 flex items-center justify-center rounded-xl border border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors flex-shrink-0"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-foreground">
            {isNew ? "Nuovo template" : form.nome || "Modifica template"}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Modello riutilizzabile per i contratti</p>
        </div>
      </div>

      <div className="space-y-4 pb-8">
        {/* Info base */}
        <div className="bg-card rounded-2xl border border-border p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Nome template *</label>
            <input 
              className="w-full border border-border rounded-xl px-3.5 py-2.5 text-sm text-foreground bg-card focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={form.nome}
              onChange={e => set("nome", e.target.value)}
              placeholder="Es. Contratto affitto breve standard"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Tipo contratto</label>
            <select 
              className="w-full border border-border rounded-xl px-3.5 py-2.5 text-sm text-foreground bg-card focus:outline-none focus:ring-2 focus:ring-primary/30"
              value={form.tipo}
              onChange={e => set("tipo", e.target.value)}
            >
              {TIPI.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>

          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-foreground">Template attivo</label>
            <button 
              onClick={() => set("attivo", !form.attivo)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.attivo ? "bg-primary" : "bg-muted"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.attivo ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        </div>

        {/* Editor */}
        <div className="bg-card rounded-2xl border border-border p-5">
          <label className="block text-sm font-medium text-foreground mb-2">Testo del contratto</label>
          
          {/* Placeholder tags */}
          <div className="mb-3 p-3 rounded-xl bg-muted/40 border border-border space-y-3">
            <p className="text-xs font-semibold text-foreground">Inserisci tag — clicca per aggiungere nel testo</p>
            {PLACEHOLDER_GROUPS.map(group => (
              <div key={group.label}>
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">{group.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {group.tags.map(({ tag, desc }) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => insertTag(tag)}
                      title={desc}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-border bg-background text-[11px] font-mono text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <textarea
            ref={textareaRef}
            className="w-full border border-border rounded-xl px-3.5 py-2.5 text-sm text-foreground bg-card focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none font-mono text-xs leading-relaxed"
            rows={18}
            value={form.contenuto}
            onChange={e => set("contenuto", e.target.value)}
            placeholder="Scrivi il testo del contratto. Clicca i tag sopra per inserirli..."
          />
        </div>

        {/* Save */}
        <div className="space-y-2">
          {saveError && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm">
              {saveError}
            </div>
          )}
          {saved && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">
              <CheckCircle className="w-4 h-4" /> Template salvato
            </div>
          )}
          <button 
            onClick={handleSave}
            disabled={!form.nome.trim() || isPending}
            className="w-full py-4 rounded-xl font-semibold text-sm disabled:opacity-40 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {isPending ? "Salvataggio..." : isNew ? "Crea template" : "Salva modifiche"}
          </button>
        </div>
      </div>
    </div>
  );
}
